import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { 
  Users, Building, UserPlus, CheckCircle, Clock, XCircle, 
  Search, Filter, Calendar, Mail, Phone, Briefcase, ArrowRight,
  Globe, MessageSquare, Video
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function AdminRegistrations() {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('registrations');
  const [registrations, setRegistrations] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedReg, setSelectedReg] = useState(null);
  const [convertForm, setConvertForm] = useState({
    organization_id: '',
    password: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [regsRes, demosRes, orgsRes] = await Promise.all([
        api.get('/platform/registrations'),
        api.get('/platform/demo-requests'),
        api.get('/platform/organizations')
      ]);
      setRegistrations(regsRes.data);
      setDemoRequests(demosRes.data || []);
      setOrganizations(orgsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (regId, newStatus, type = 'registration') => {
    try {
      const endpoint = type === 'demo' 
        ? `/platform/demo-requests/${regId}` 
        : `/platform/registrations/${regId}`;
      await api.patch(endpoint, null, { params: { status: newStatus } });
      
      if (type === 'demo') {
        setDemoRequests(prev => prev.map(r => r.id === regId ? { ...r, status: newStatus } : r));
      } else {
        setRegistrations(prev => prev.map(r => r.id === regId ? { ...r, status: newStatus } : r));
      }
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleConvert = async () => {
    if (!convertForm.organization_id || !convertForm.password) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await api.post(`/platform/registrations/${selectedReg.id}/convert`, null, {
        params: {
          organization_id: convertForm.organization_id,
          password: convertForm.password
        }
      });
      toast.success('Registration converted to user account');
      setConvertDialogOpen(false);
      setSelectedReg(null);
      setConvertForm({ organization_id: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert registration');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'contacted': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'converted': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'scheduled': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'completed': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'rejected': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'contacted': return <Mail className="w-4 h-4" />;
      case 'scheduled': return <Calendar className="w-4 h-4" />;
      case 'converted': case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredRegistrations = registrations.filter(r => {
    const matchesSearch = 
      r.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.organization_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredDemoRequests = demoRequests.filter(r => {
    const matchesSearch = 
      r.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const regStats = {
    total: registrations.length,
    pending: registrations.filter(r => r.status === 'pending').length,
    contacted: registrations.filter(r => r.status === 'contacted').length,
    converted: registrations.filter(r => r.status === 'converted').length,
  };

  const demoStats = {
    total: demoRequests.length,
    pending: demoRequests.filter(r => r.status === 'pending').length,
    scheduled: demoRequests.filter(r => r.status === 'scheduled').length,
    completed: demoRequests.filter(r => r.status === 'completed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-registrations-container" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-rajdhani font-bold text-3xl text-white text-shadow-neon flex items-center gap-3">
          <UserPlus className="w-8 h-8 text-indigo-400" style={{ filter: 'drop-shadow(0 0 8px rgba(99, 102, 241, 0.6))' }} />
          Registrations & Demo Requests
        </h1>
        <p className="text-zinc-500 font-inter mt-1">Manage registration requests and demo bookings from the marketing website</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-zinc-900/50 border border-white/10 p-1">
          <TabsTrigger 
            value="registrations" 
            className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white px-6"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Registrations ({registrations.length})
          </TabsTrigger>
          <TabsTrigger 
            value="demo-requests" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white px-6"
          >
            <Video className="w-4 h-4 mr-2" />
            Demo Requests ({demoRequests.length})
          </TabsTrigger>
        </TabsList>

        {/* Registrations Tab */}
        <TabsContent value="registrations" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass rounded-xl p-4 text-center">
              <p className="text-3xl font-rajdhani font-bold text-white">{regStats.total}</p>
              <p className="text-xs text-zinc-500">Total</p>
            </div>
            <div className="glass rounded-xl p-4 text-center border-l-2 border-yellow-500">
              <p className="text-3xl font-rajdhani font-bold text-yellow-400">{regStats.pending}</p>
              <p className="text-xs text-zinc-500">Pending</p>
            </div>
            <div className="glass rounded-xl p-4 text-center border-l-2 border-blue-500">
              <p className="text-3xl font-rajdhani font-bold text-blue-400">{regStats.contacted}</p>
              <p className="text-xs text-zinc-500">Contacted</p>
            </div>
            <div className="glass rounded-xl p-4 text-center border-l-2 border-emerald-500">
              <p className="text-3xl font-rajdhani font-bold text-emerald-400">{regStats.converted}</p>
              <p className="text-xs text-zinc-500">Converted</p>
            </div>
          </div>

          {/* Filters */}
          <div className="glass rounded-2xl p-5">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search by name, email, or organization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-950/50 border-white/10 text-white"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48 bg-zinc-950/50 border-white/10 text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="converted">Converted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Registrations List */}
          <div className="glass rounded-2xl overflow-hidden">
            <div className="scroll-container">
              <table className="w-full min-w-[700px]">
                <thead className="bg-zinc-900/50">
                  <tr className="text-left text-zinc-500 text-xs font-inter">
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Organization</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Registered</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredRegistrations.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-12 text-center text-zinc-500">
                        No registrations found
                      </td>
                    </tr>
                  ) : (
                    filteredRegistrations.map((reg) => (
                      <tr key={reg.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-inter font-medium text-white">{reg.full_name}</p>
                            <p className="text-xs text-zinc-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {reg.email}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-zinc-300">{reg.organization_name || '-'}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${getStatusColor(reg.status)}`}>
                            {getStatusIcon(reg.status)}
                            {reg.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-xs text-zinc-500">
                          {new Date(reg.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Select 
                              value={reg.status} 
                              onValueChange={(val) => handleStatusChange(reg.id, val)}
                            >
                              <SelectTrigger className="w-32 h-8 bg-zinc-900 border-white/10 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-white/10 text-white">
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="contacted">Contacted</SelectItem>
                                <SelectItem value="converted">Converted</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                              </SelectContent>
                            </Select>
                            {reg.status !== 'converted' && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedReg(reg);
                                  setConvertDialogOpen(true);
                                }}
                                className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8"
                              >
                                <ArrowRight className="w-3 h-3 mr-1" />
                                Convert
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Demo Requests Tab */}
        <TabsContent value="demo-requests" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass rounded-xl p-4 text-center">
              <p className="text-3xl font-rajdhani font-bold text-white">{demoStats.total}</p>
              <p className="text-xs text-zinc-500">Total Requests</p>
            </div>
            <div className="glass rounded-xl p-4 text-center border-l-2 border-yellow-500">
              <p className="text-3xl font-rajdhani font-bold text-yellow-400">{demoStats.pending}</p>
              <p className="text-xs text-zinc-500">Pending</p>
            </div>
            <div className="glass rounded-xl p-4 text-center border-l-2 border-purple-500">
              <p className="text-3xl font-rajdhani font-bold text-purple-400">{demoStats.scheduled}</p>
              <p className="text-xs text-zinc-500">Scheduled</p>
            </div>
            <div className="glass rounded-xl p-4 text-center border-l-2 border-emerald-500">
              <p className="text-3xl font-rajdhani font-bold text-emerald-400">{demoStats.completed}</p>
              <p className="text-xs text-zinc-500">Completed</p>
            </div>
          </div>

          {/* Filters */}
          <div className="glass rounded-2xl p-5">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search by name, email, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-950/50 border-white/10 text-white"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48 bg-zinc-950/50 border-white/10 text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Demo Requests List */}
          <div className="glass rounded-2xl overflow-hidden">
            <div className="scroll-container">
              <table className="w-full min-w-[900px]">
                <thead className="bg-zinc-900/50">
                  <tr className="text-left text-zinc-500 text-xs font-inter">
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Preferred Time</th>
                    <th className="px-4 py-3">Timezone</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Requested</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredDemoRequests.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center text-zinc-500">
                        No demo requests found
                      </td>
                    </tr>
                  ) : (
                    filteredDemoRequests.map((demo) => (
                      <tr key={demo.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-inter font-medium text-white">{demo.full_name}</p>
                            <p className="text-xs text-zinc-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {demo.email}
                            </p>
                            <p className="text-xs text-zinc-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {demo.mobile}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-zinc-300 flex items-center gap-1">
                            <Building className="w-3 h-3" /> {demo.company}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-zinc-300">
                            <p className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-purple-400" /> {demo.preferred_day}
                            </p>
                            <p className="text-xs text-zinc-500">{demo.preferred_time}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-xs text-zinc-400 flex items-center gap-1">
                            <Globe className="w-3 h-3" /> {demo.timezone?.split(' ')[0] || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${getStatusColor(demo.status)}`}>
                            {getStatusIcon(demo.status)}
                            {demo.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-xs text-zinc-500">
                          {new Date(demo.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <Select 
                            value={demo.status} 
                            onValueChange={(val) => handleStatusChange(demo.id, val, 'demo')}
                          >
                            <SelectTrigger className="w-32 h-8 bg-zinc-900 border-white/10 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-white/10 text-white">
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="scheduled">Scheduled</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes Section */}
          {filteredDemoRequests.some(d => d.notes) && (
            <div className="glass rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-purple-400" />
                Request Notes
              </h3>
              <div className="space-y-3">
                {filteredDemoRequests.filter(d => d.notes).map((demo) => (
                  <div key={demo.id} className="p-3 bg-zinc-900/50 rounded-lg">
                    <p className="text-xs text-purple-400 mb-1">{demo.full_name} - {demo.company}</p>
                    <p className="text-sm text-zinc-300">{demo.notes}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Convert Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl">Convert to User Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <p className="text-sm text-zinc-400 mb-2">
                Converting: <span className="text-white">{selectedReg?.full_name}</span> ({selectedReg?.email})
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Assign to Organization</Label>
              <Select 
                value={convertForm.organization_id} 
                onValueChange={(val) => setConvertForm({...convertForm, organization_id: val})}
              >
                <SelectTrigger className="bg-zinc-950 border-white/10">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Set Password</Label>
              <Input 
                type="password"
                value={convertForm.password}
                onChange={(e) => setConvertForm({...convertForm, password: e.target.value})}
                placeholder="Enter password for new user"
                className="bg-zinc-950 border-white/10 text-white"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setConvertDialogOpen(false)} className="border-white/10">
                Cancel
              </Button>
              <Button onClick={handleConvert} className="bg-emerald-600 hover:bg-emerald-700">
                Convert to User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
